import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg, money, STATES } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Field, Notice, PageShell } from "@/components/site/Layout";

export function Cart() {
  const { cart, setCart, config, user } = useStore();
  const [busy, setBusy] = useState(false);
  const setQty = async (pid, qty) => {
    setBusy(true);
    try { setCart((await api.post("/cart/items", { product_id: pid, qty })).data); } finally { setBusy(false); }
  };
  const q = cart.quote;
  return (
    <PageShell narrow>
      <h1 className="text-4xl sm:text-5xl mb-8">Your cart</h1>
      {!q ? (
        <div className="hn-card p-10 text-center" data-testid="cart-empty"><p className="mb-6">Your cart is empty.</p><Link to="/shop" className="hn-btn">View the product</Link></div>
      ) : (
        <>
          <ul className="divide-y divide-[#E5DEC9] border-y border-[#E5DEC9]">
            {q.lines.map((l) => (
              <li key={l.product_id} className="py-5 flex flex-wrap items-center justify-between gap-4" data-testid="cart-line">
                <div><p className="font-semibold">{l.name}</p><p className="text-xs text-[#5b6b62]">{l.net_weight} · {money(l.unit_price_cents)} each</p></div>
                <div className="flex items-center gap-3">
                  <label className="sr-only" htmlFor={`q-${l.product_id}`}>Quantity</label>
                  <select id={`q-${l.product_id}`} className="hn-input !w-20" value={l.qty} disabled={busy} onChange={(e) => setQty(l.product_id, +e.target.value)} data-testid="cart-qty-select">
                    {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => <option key={n}>{n}</option>)}
                  </select>
                  <button className="text-sm underline" onClick={() => setQty(l.product_id, 0)} disabled={busy} data-testid="cart-remove-btn">Remove</button>
                  <span className="w-24 text-right tabular-nums">{money(l.line_total_cents)}</span>
                </div>
              </li>
            ))}
          </ul>
          <div className="flex justify-between mt-6 text-lg"><span>Merchandise subtotal</span><span className="tabular-nums" data-testid="cart-subtotal">{money(q.subtotal_cents)}</span></div>
          <p className="text-xs text-[#5b6b62] mt-1">Delivery and any promotional credit are calculated at checkout.</p>
          <div className="mt-8">
            {config?.mode === "PRELAUNCH" ? <Notice tone="warn" testid="cart-prelaunch-notice">HONEY NUTS isn't available to purchase yet. Your cart is saved for when we launch.</Notice>
              : <Link to={user ? "/checkout" : "/login?next=/checkout"} className="hn-btn" data-testid="cart-checkout-btn">Continue to checkout</Link>}
          </div>
        </>
      )}
    </PageShell>
  );
}

const EMPTY = { name: "", line1: "", line2: "", suburb: "", state: "NSW", postcode: "", phone: "" };

const AddressFields = ({ a, set }) => (
  <div className="grid sm:grid-cols-2 gap-4">
    {[["line1", "Street address", "address-line1"], ["line2", "Apartment, unit (optional)", "address-line2"], ["suburb", "Suburb", "address-level2"]].map(([k, l, ac]) => (
      <Field key={k} label={l} id={`a-${k}`}><input id={`a-${k}`} autoComplete={ac} className="hn-input" value={a[k] || ""} onChange={(e) => set({ ...a, [k]: e.target.value })} data-testid={`address-${k}-input`} /></Field>
    ))}
    <Field label="State" id="a-state"><select id="a-state" className="hn-input" value={a.state} onChange={(e) => set({ ...a, state: e.target.value })} data-testid="address-state-select">{STATES.map((s) => <option key={s}>{s}</option>)}</select></Field>
    <Field label="Postcode" id="a-pc"><input id="a-pc" inputMode="numeric" maxLength={4} autoComplete="postal-code" className="hn-input" value={a.postcode} onChange={(e) => set({ ...a, postcode: e.target.value.replace(/\D/g, "") })} data-testid="address-postcode-input" /></Field>
  </div>
);
export { AddressFields };

export function Checkout() {
  const { user, config, refreshUser } = useStore();
  const nav = useNavigate();
  const [addrs, setAddrs] = useState([]);
  const [a, setA] = useState(EMPTY);
  const [f, setF] = useState({ name: "", phone: "", use_credit: false, accept_terms: false, marketing_opt_in: false, age_declaration: false });
  const [q, setQ] = useState(null);
  const [st, setSt] = useState({});
  useEffect(() => {
    if (user === false) nav("/login?next=/checkout");
    if (user) {
      setF((x) => ({ ...x, name: x.name || user.name || "", phone: x.phone || user.phone || "" }));
      api.get("/account/addresses").then((r) => { setAddrs(r.data); if (r.data[0]) setA({ ...EMPTY, ...r.data[0] }); });
    }
  }, [user, nav]);
  const ready = a.line1 && a.suburb && /^\d{4}$/.test(a.postcode);
  const addrKey = useMemo(() => JSON.stringify([a.state, a.postcode, a.line1, a.suburb, f.use_credit]), [a, f.use_credit]);
  useEffect(() => {
    if (!ready || !user) return;
    const t = setTimeout(() => {
      api.post("/checkout/quote", { address: { ...a, name: f.name || "x" }, use_credit: f.use_credit }).then((r) => setQ(r.data)).catch((e) => setQ({ error: errMsg(e) }));
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addrKey, ready, user]);
  const pay = async (e) => {
    e.preventDefault();
    setSt({ loading: true });
    try {
      const { data } = await api.post("/checkout/session", { ...f, address: { ...a, name: f.name } });
      window.location.href = data.checkout_url;
    } catch (err) {
      setSt({ error: errMsg(err) });
    }
  };
  if (!user) return <PageShell><p>Loading…</p></PageShell>;
  const blockers = q?.blockers || [];
  return (
    <PageShell>
      <h1 className="text-4xl sm:text-5xl mb-8">Checkout</h1>
      {!user.email_verified && (
        <Notice tone="warn" testid="checkout-unverified">Please verify {user.email} before ordering. <button className="underline" onClick={() => api.post("/auth/resend-verification").then(() => refreshUser())} data-testid="resend-verification-btn">Resend link</button></Notice>
      )}
      <form onSubmit={pay} className="grid lg:grid-cols-5 gap-10 mt-6">
        <div className="lg:col-span-3 grid gap-6">
          <section className="grid gap-4">
            <h2 className="text-2xl">Contact</h2>
            <p className="text-sm">Order emails go to <strong data-testid="checkout-email">{user.email}</strong>{user.email_verified ? " (verified)" : " (not verified)"}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name" id="c-name"><input id="c-name" autoComplete="name" className="hn-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="checkout-name-input" /></Field>
              <Field label="Phone for delivery" id="c-phone"><input id="c-phone" type="tel" autoComplete="tel" className="hn-input" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} required data-testid="checkout-phone-input" /></Field>
            </div>
          </section>
          <section className="grid gap-4">
            <h2 className="text-2xl">Delivery address</h2>
            {addrs.length > 0 && (
              <select className="hn-input" onChange={(e) => setA({ ...EMPTY, ...addrs[+e.target.value] })} aria-label="Saved addresses" data-testid="checkout-saved-address-select">
                {addrs.map((x, i) => <option key={x.id} value={i}>{x.label || x.line1}, {x.suburb} {x.postcode}</option>)}
              </select>
            )}
            <AddressFields a={a} set={setA} />
            {q?.zone && <p className="text-sm" data-testid="checkout-delivery-method">Delivery method: {q.zone.name}. {q.zone.estimate}</p>}
          </section>
          <section className="grid gap-3 text-sm">
            {user.credit_available_cents > 0 && <label className="flex gap-3"><input type="checkbox" className="accent-[#173B2D]" checked={f.use_credit} onChange={(e) => setF({ ...f, use_credit: e.target.checked })} data-testid="checkout-use-credit-checkbox" />Use my promotional credit ({money(user.credit_available_cents)} available, merchandise only)</label>}
            {config?.age_check?.enabled && <label className="flex gap-3"><input type="checkbox" className="accent-[#173B2D]" checked={f.age_declaration} onChange={(e) => setF({ ...f, age_declaration: e.target.checked })} data-testid="checkout-age-checkbox" />{config.age_check.wording} (self-declaration)</label>}
            <label className="flex gap-3"><input type="checkbox" className="accent-[#173B2D]" checked={f.accept_terms} onChange={(e) => setF({ ...f, accept_terms: e.target.checked })} required data-testid="checkout-terms-checkbox" /><span>I agree to the <Link to="/policies/terms" className="underline" target="_blank">terms</Link>, <Link to="/policies/shipping" className="underline" target="_blank">shipping</Link> and <Link to="/policies/returns" className="underline" target="_blank">returns</Link> policies.</span></label>
            <label className="flex gap-3"><input type="checkbox" className="accent-[#173B2D]" checked={f.marketing_opt_in} onChange={(e) => setF({ ...f, marketing_opt_in: e.target.checked })} data-testid="checkout-marketing-checkbox" />Email me HONEY NUTS news (optional, separate from order emails)</label>
          </section>
        </div>
        <aside className="lg:col-span-2 hn-card p-6 h-fit" aria-label="Order summary" data-testid="checkout-summary">
          <h2 className="text-2xl mb-4">Summary</h2>
          {!ready ? <p className="text-sm text-[#5b6b62]">Enter your delivery address to see delivery and total.</p> : !q ? <p className="text-sm">Calculating…</p> : q.error ? <Notice tone="error">{q.error}</Notice> : (
            <dl className="text-sm grid gap-2">
              {q.lines.map((l) => <div key={l.product_id} className="flex justify-between"><dt>{l.name} × {l.qty} <span className="text-[#5b6b62]">@ {money(l.unit_price_cents)}</span></dt><dd className="tabular-nums">{money(l.line_total_cents)}</dd></div>)}
              <div className="flex justify-between border-t border-[#E5DEC9] pt-2"><dt>Merchandise subtotal</dt><dd className="tabular-nums">{money(q.subtotal_cents)}</dd></div>
              <div className="flex justify-between"><dt>Discounts</dt><dd className="tabular-nums">{money(q.discount_cents)}</dd></div>
              <div className="flex justify-between"><dt>Promotional credit</dt><dd className="tabular-nums" data-testid="summary-credit">-{money(q.credit_cents)}</dd></div>
              <div className="flex justify-between"><dt>Delivery</dt><dd className="tabular-nums" data-testid="summary-shipping">{q.shipping_cents == null ? "Not available" : money(q.shipping_cents)}</dd></div>
              {q.tax?.gst_included_cents > 0 && <div className="flex justify-between"><dt>{q.tax.label}</dt><dd className="tabular-nums">{money(q.tax.gst_included_cents)}</dd></div>}
              <div className="flex justify-between border-t border-[#E5DEC9] pt-3 text-lg font-semibold"><dt>Total (AUD)</dt><dd className="tabular-nums" data-testid="summary-total">{money(q.total_cents)}</dd></div>
              {q.zone && !q.zone.confirmed && <p className="text-xs text-[#7a4d18]">Delivery rate is a test rate pending owner confirmation.</p>}
            </dl>
          )}
          {blockers.map((b) => <Notice key={b} tone="warn" testid="checkout-blocker">{b}</Notice>)}
          {st.error && <div className="mt-3"><Notice tone="error" testid="checkout-error">{st.error}</Notice></div>}
          <button className="hn-btn w-full mt-6" disabled={st.loading || !q || q.error || blockers.length > 0 || !f.accept_terms} data-testid="checkout-pay-btn">{st.loading ? "Opening secure payment…" : "Pay securely with Stripe"}</button>
          <p className="text-xs text-[#5b6b62] mt-3">You'll be taken to Stripe's secure payment page. We never see or store your card details.</p>
        </aside>
      </form>
    </PageShell>
  );
}

export function PaymentResult({ cancelled }) {
  const [sp] = useSearchParams();
  const { refreshCart } = useStore();
  const [s, setS] = useState(null);
  const tries = useRef(0);
  useEffect(() => {
    if (cancelled) {
      const oid = sp.get("order");
      if (oid) api.post(`/payments/cancel/${oid}`).then((r) => setS(r.data)).catch(() => setS({ payment_status: "cancelled" }));
      return;
    }
    let t;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sp.get("session_id")}`);
        setS(data);
        if (data.payment_status === "paid") { refreshCart(); return; }
      } catch (e) { setS({ error: errMsg(e) }); return; }
      if (++tries.current < 15) t = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(t);
  }, [sp, cancelled, refreshCart]);
  return (
    <PageShell narrow>
      <h1 className="text-4xl mb-6">{cancelled ? "Checkout cancelled" : "Payment"}</h1>
      {cancelled ? <Notice testid="payment-cancelled">No payment was taken. Your cart is still saved.</Notice>
        : !s ? <p aria-busy="true">Confirming your payment…</p>
        : s.error ? <Notice tone="error">{s.error}</Notice>
        : s.payment_status === "paid" ? <Notice testid="payment-success">{s.is_test ? "TEST payment confirmed — not a real purchase. " : ""}Thank you! Order {s.number} is confirmed. Your invoice will be emailed shortly.</Notice>
        : <Notice tone="warn" testid="payment-pending">We're still waiting for confirmation from the payment provider (status: {s.payment_status}). We'll email you once it's confirmed.</Notice>}
      <div className="mt-8 flex gap-3">
        {s?.order_id && <Link to={`/account/orders/${s.order_id}`} className="hn-btn" data-testid="view-order-btn">View order</Link>}
        <Link to={cancelled ? "/cart" : "/"} className="hn-btn hn-btn-outline">{cancelled ? "Back to cart" : "Home"}</Link>
      </div>
    </PageShell>
  );
}
